/**
 * useWabaCall — входящие звонки на общий номер WhatsApp Business (DEV-2418).
 *
 * Отличия от useTelegramCall:
 * - номер общий, но входящее приходит только менеджеру клиента, а без менеджера —
 *   руководителям (DEV-2438). Звонок забирает первый нажавший «Принять»,
 *   остальным сервер шлёт STATE `taken`;
 * - исходящих нет — Meta запрещает их для бизнес-номеров Вьетнама (DEV-1688);
 * - сервер шлёт offer, ICE и STATE принявшему во ВСЕ его сокеты, то есть во все
 *   открытые вкладки. Отвечает только вкладка, где нажали «Принять»
 *   (acceptedHere), остальные гасят баннер;
 * - занятый менеджер новое входящее не отклоняет: отказ закрыл бы звонок у
 *   всех. Оно ждёт в очереди и показывается, как только окно освободится.
 *   Пока ждёт, оно видно в окне строкой «ещё звонят» с кнопкой «Отклонить»
 *   (DEV-2484); принять его до конца текущего звонка нельзя — удержания нет;
 * - потерянный ENDED (обрыв сокета, выкат сервиса) не вешает баннер навсегда:
 *   у звонка и у соединения свои таймауты. Сам обрыв сокета звонок не снимает:
 *   после переподключения «Принять» сработает — сервер шлёт по владельцу.
 *
 * Коды — из internal/waba/calls/signaling.go в messenger-providers.
 */
import { ref, computed, readonly } from 'vue'
import { sendWs } from '@/features/messenger/composables/messengerWsBus'
import { useAuthStore } from '@/features/auth/stores/auth.store'

const CMD_ACCEPT = 'WABA_CALL_ACCEPT'
const CMD_DECLINE = 'WABA_CALL_DECLINE'
const CMD_SDP_ANSWER = 'WABA_CALL_SDP_ANSWER'
const CMD_ICE = 'WABA_CALL_ICE'
const CMD_HANGUP = 'WABA_CALL_HANGUP'

// Сервер считает неотвеченный звонок пропущенным через 60 с (ringTimeout в
// manager.go); баннер живёт чуть дольше, чтобы не опередить его ENDED.
export const RING_TIMEOUT_MS = 75_000
// После «Принять» сервер ждёт медиа 15 с (mediaTimeout) и закрывает звонок.
// Отказ в accept (коллега успел раньше) сервер не присылает вовсе.
export const CONNECT_TIMEOUT_MS = 30_000

// Как у Telegram: у сервера публичный адрес балансировщика, браузеру хватает STUN.
const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }]

// Отказ в доступе к микрофону отличаем от сбоя WebRTC: тексты разные.
const MIC_ERRORS = new Set([
  'NotAllowedError',
  'NotFoundError',
  'NotReadableError',
  'SecurityError',
])

// Сервер закрыл звонок до разговора по своей ошибке — менеджеру нужна причина.
// peer_terminated (клиент положил трубку) и hangup ошибкой не считаются.
const QUIET_END_REASONS = new Set(['peer_terminated', 'hangup'])

export type WabaCallState = 'idle' | 'incoming' | 'connecting' | 'active' | 'ended'

export interface WsWabaIncomingPayload {
  call_id: string
  peer_phone: string
  peer_name?: string
  client_id?: string
}

export interface WsWabaStatePayload {
  call_id: string
  state: string
  peer_name?: string
  owner_id?: string
}

export interface WsWabaEndedPayload {
  call_id: string
  state: string
  discard_reason?: string
  duration_sec?: number
}

// --- модульный синглтон: одно окно звонка на вкладку ---
const callState = ref<WabaCallState>('idle')
const callId = ref<string | null>(null)
const peerName = ref('')
const peerPhone = ref('')
const clientId = ref<string | null>(null)
const isMuted = ref(false)
const durationSeconds = ref(0)
const callError = ref('')
const discardReason = ref('')
// Для панели истории: какой звонок завершился и когда готов разбор записи.
const endedCallId = ref<string | null>(null)
const analysisReadyAt = ref(0)

// Звонок принят именно в этой вкладке — только она отвечает на offer.
let acceptedHere = false
// Входящие, пришедшие, пока окно занято: видны в окне и показываются по очереди.
const queue = ref<{ data: WsWabaIncomingPayload; at: number }[]>([])
let stateTimer: ReturnType<typeof setTimeout> | null = null
let pc: RTCPeerConnection | null = null
let localStream: MediaStream | null = null
let remoteAudio: HTMLAudioElement | null = null
const pendingRemoteIce: RTCIceCandidateInit[] = []
let durationTimer: ReturnType<typeof setInterval> | null = null

function startDurationTimer() {
  stopDurationTimer()
  const answeredAt = Date.now()
  durationTimer = setInterval(() => {
    durationSeconds.value = Math.floor((Date.now() - answeredAt) / 1000)
  }, 1000)
}

function stopDurationTimer() {
  if (durationTimer) {
    clearInterval(durationTimer)
    durationTimer = null
  }
}

function clearStateTimer() {
  if (stateTimer) {
    clearTimeout(stateTimer)
    stateTimer = null
  }
}

function armStateTimer(ms: number, onFire: () => void) {
  clearStateTimer()
  stateTimer = setTimeout(() => {
    stateTimer = null
    onFire()
  }, ms)
}

function teardownPeer() {
  stopDurationTimer()
  if (pc) {
    pc.ontrack = null
    pc.onicecandidate = null
    pc.onconnectionstatechange = null
    pc.close()
    pc = null
  }
  if (localStream) {
    for (const track of localStream.getTracks()) track.stop()
    localStream = null
  }
  if (remoteAudio) {
    remoteAudio.srcObject = null
    remoteAudio = null
  }
  pendingRemoteIce.length = 0
}

/** Сбрасывает окно в idle; очередь не трогает. */
function clearCall() {
  teardownPeer()
  clearStateTimer()
  acceptedHere = false
  callState.value = 'idle'
  callId.value = null
  peerName.value = ''
  peerPhone.value = ''
  clientId.value = null
  isMuted.value = false
  durationSeconds.value = 0
  callError.value = ''
  discardReason.value = ''
}

function showIncoming(data: WsWabaIncomingPayload, receivedAt = Date.now()) {
  clearCall()
  callId.value = data.call_id
  peerPhone.value = data.peer_phone
  peerName.value = data.peer_name ?? ''
  clientId.value = data.client_id || null
  callState.value = 'incoming'
  // ENDED мог потеряться — без таймаута баннер висел бы до перезагрузки.
  // Звонку из очереди — остаток его времени, а не новые 75 с.
  armStateTimer(Math.max(0, RING_TIMEOUT_MS - (Date.now() - receivedAt)), dropCurrent)
}

/** Окно освободилось — показать следующее ещё звонящее входящее. */
function showNext() {
  const now = Date.now()
  queue.value = queue.value.filter((q) => now - q.at < RING_TIMEOUT_MS)
  const next = queue.value.shift()
  if (next) showIncoming(next.data, next.at)
}

/** Баннер текущего звонка больше не нужен — дальше по очереди. */
function dropCurrent() {
  clearCall()
  showNext()
}

function forget(id: string) {
  queue.value = queue.value.filter((q) => q.data.call_id !== id)
}

function endHere(reason = '') {
  clearStateTimer()
  endedCallId.value = callId.value
  if (reason) discardReason.value = reason
  callState.value = 'ended'
  teardownPeer()
}

/**
 * Поднимает браузерную ногу. null — пока браузер спрашивал микрофон, звонок
 * закончился: поток сразу гасим, иначе микрофон остался бы занят.
 */
async function setupPeer(id: string): Promise<RTCPeerConnection | null> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
  if (callId.value !== id || !acceptedHere || callState.value !== 'connecting') {
    for (const track of stream.getTracks()) track.stop()
    return null
  }
  localStream = stream
  const peer = new RTCPeerConnection({ iceServers: ICE_SERVERS })
  pc = peer

  for (const track of stream.getAudioTracks()) {
    track.enabled = !isMuted.value
    peer.addTrack(track, stream)
  }

  peer.ontrack = (ev) => {
    if (!remoteAudio) {
      remoteAudio = new Audio()
      remoteAudio.autoplay = true
    }
    remoteAudio.srcObject = ev.streams[0] ?? new MediaStream([ev.track])
    // play() идёт после клика «Принять»; отказ autoplay не роняем.
    void remoteAudio.play().catch(() => {})
  }

  peer.onicecandidate = (ev) => {
    if (!ev.candidate) return
    sendWs(CMD_ICE, {
      call_id: id,
      candidate: ev.candidate.candidate,
      sdp_mid: ev.candidate.sdpMid ?? '',
      sdp_mline_index: ev.candidate.sdpMLineIndex ?? null,
    })
  }

  // Медиапуть умер — кладём трубку сами: ENDED может не дойти при обрыве WS.
  peer.onconnectionstatechange = () => {
    if (pc === peer && peer.connectionState === 'failed' && callState.value !== 'ended') {
      callError.value = 'connection_failed'
      if (callId.value) sendWs(CMD_HANGUP, { call_id: callId.value })
      endHere()
    }
  }

  return peer
}

const isActive = computed(() => callState.value === 'connecting' || callState.value === 'active')

const waitingCalls = computed(() => queue.value.map((q) => q.data))

const formattedDuration = computed(() => {
  const mins = Math.floor(durationSeconds.value / 60)
  const secs = durationSeconds.value % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
})

export function useWabaCall() {
  function acceptIncoming() {
    if (callState.value !== 'incoming' || !callId.value) return
    acceptedHere = true
    callState.value = 'connecting'
    // Сервер молчит, если звонок уже забрали или он умер, — не висим вечно.
    armStateTimer(CONNECT_TIMEOUT_MS, () => {
      if (callState.value !== 'connecting') return
      callError.value = 'connection_failed'
      if (callId.value) sendWs(CMD_HANGUP, { call_id: callId.value })
      endHere()
    })
    if (!sendWs(CMD_ACCEPT, { call_id: callId.value })) {
      callError.value = 'ws_disconnected'
      endHere()
    }
  }

  /** Отклоняет звонок у Meta — у всех менеджеров, а не только у себя. */
  function declineIncoming() {
    if (callState.value !== 'incoming' || !callId.value) return
    sendWs(CMD_DECLINE, { call_id: callId.value })
    dropCurrent()
  }

  /** Отклоняет звонок из списка «ещё звонят»; текущий звонок не трогает. */
  function declineWaiting(id: string) {
    if (!queue.value.some((q) => q.data.call_id === id)) return
    sendWs(CMD_DECLINE, { call_id: id })
    forget(id)
  }

  function hangup() {
    if (callId.value) sendWs(CMD_HANGUP, { call_id: callId.value })
    endHere()
  }

  function toggleMute() {
    isMuted.value = !isMuted.value
    for (const track of localStream?.getAudioTracks() ?? []) track.enabled = !isMuted.value
  }

  /** Закрыть окно завершённого звонка; следом — следующее входящее из очереди. */
  function closeCall() {
    dropCurrent()
  }

  /** Полный сброс с очередью: выход из CRM, размонтирование лэйаута. */
  function resetAll() {
    queue.value = []
    clearCall()
  }

  // --- события сервера, маршрутизирует useMessengerWs ---

  function handleIncoming(data: WsWabaIncomingPayload) {
    if (data.call_id === callId.value || queue.value.some((q) => q.data.call_id === data.call_id)) {
      return
    }
    if (callState.value === 'incoming' || isActive.value) {
      queue.value.push({ data, at: Date.now() })
      // ENDED о пропуске мог потеряться — строка «ещё звонят» не должна висеть.
      setTimeout(() => forget(data.call_id), RING_TIMEOUT_MS)
      return
    }
    showIncoming(data)
  }

  async function handleSdpOffer(data: { call_id: string; sdp: string }) {
    if (data.call_id !== callId.value || !acceptedHere) return
    try {
      const peer = await setupPeer(data.call_id)
      if (!peer) return
      await peer.setRemoteDescription({ type: 'offer', sdp: data.sdp })
      for (const cand of pendingRemoteIce.splice(0)) {
        await peer.addIceCandidate(cand).catch(() => {})
      }
      const answer = await peer.createAnswer()
      await peer.setLocalDescription(answer)
      // Звонок закончили, пока шли await: соединение уже закрыто teardownPeer.
      if (pc !== peer) return
      sendWs(CMD_SDP_ANSWER, { call_id: data.call_id, sdp: answer.sdp })
    } catch (e) {
      callError.value =
        e instanceof DOMException && MIC_ERRORS.has(e.name) ? 'mic_denied' : 'webrtc_error'
      hangup()
    }
  }

  async function handleRemoteIce(data: {
    call_id: string
    candidate: string
    sdp_mid?: string
    sdp_mline_index?: number | null
  }) {
    if (data.call_id !== callId.value || !acceptedHere) return
    const init: RTCIceCandidateInit = {
      candidate: data.candidate,
      sdpMid: data.sdp_mid ?? null,
      sdpMLineIndex: data.sdp_mline_index ?? null,
    }
    if (pc?.remoteDescription) {
      await pc.addIceCandidate(init).catch(() => {})
    } else {
      pendingRemoteIce.push(init)
    }
  }

  function handleState(data: WsWabaStatePayload) {
    // Любое состояние звонка из очереди значит, что его уже взяли.
    if (data.call_id !== callId.value) {
      forget(data.call_id)
      return
    }
    // Звонок взял другой менеджер или я сам в другой вкладке — баннер гасим.
    // `taken` с чужим owner_id гасит и нажатое «Принять»: коллега успел раньше,
    // а проигравшему сервер больше ничего не пришлёт.
    const takenByOther = data.state === 'taken' && data.owner_id !== useAuthStore().user?.id
    if (!acceptedHere || takenByOther) {
      dropCurrent()
      return
    }
    if (data.peer_name) peerName.value = data.peer_name
    if (data.state === 'active' && callState.value === 'connecting') {
      clearStateTimer()
      callState.value = 'active'
      startDurationTimer()
    }
  }

  function handleEnded(data: WsWabaEndedPayload) {
    if (data.call_id !== callId.value) {
      forget(data.call_id)
      return
    }
    // Неотвеченный (пропущен, отклонён коллегой) — просто убираем баннер.
    if (!acceptedHere) {
      dropCurrent()
      return
    }
    const reason = data.discard_reason ?? ''
    // Сорвалось до разговора по вине сервера или Meta — показать причину.
    if (callState.value === 'connecting' && !callError.value && !QUIET_END_REASONS.has(reason)) {
      callError.value = 'connection_failed'
    }
    if (typeof data.duration_sec === 'number') durationSeconds.value = data.duration_sec
    endHere(reason)
  }

  function handleAnalysisCompleted(data: { call_id: string }) {
    if (!data.call_id) return
    endedCallId.value = data.call_id
    analysisReadyAt.value = Date.now()
  }

  return {
    callState: readonly(callState),
    callId: readonly(callId),
    peerName: readonly(peerName),
    peerPhone: readonly(peerPhone),
    clientId: readonly(clientId),
    isMuted: readonly(isMuted),
    durationSeconds: readonly(durationSeconds),
    callError: readonly(callError),
    discardReason: readonly(discardReason),
    endedCallId: readonly(endedCallId),
    analysisReadyAt: readonly(analysisReadyAt),
    isActive,
    waitingCalls,
    formattedDuration,
    acceptIncoming,
    declineIncoming,
    declineWaiting,
    hangup,
    toggleMute,
    closeCall,
    resetAll,
    handleIncoming,
    handleSdpOffer,
    handleRemoteIce,
    handleState,
    handleEnded,
    handleAnalysisCompleted,
  }
}
